import { FC, useEffect, useRef } from 'react';

import classNames from 'classnames';

import {
    ActionBar,
    BottomSheet,
    Button,
    InputSize,
    Text,
    TooltipHover,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';
import {
    ChevronWide2OutlinedSize24,
    ChevronWide3OutlinedSize24,
    ChevronWideOutlinedSize24,
} from '@hh.ru/magritte-ui/icon';

import useOnOffState from 'hooks/common/useOnOffState';
import { useMultiCreationBidBudgetValidation } from 'hooks/multiCreation/useMultiCreationBidBudgetValidation';
import useTrls from 'hooks/translation/useTrls';

import { MoneyInput } from 'components/common/MoneyInput/MoneyInput';
import { fetchMultiCreationBidRating } from 'components/MultiCreationAuction/api/fetchMultiCreationBidRating';

import { BidFrequencyStatus } from 'store/models/auctionData';
import {
    selectMultiCreationBidFrequencyStatusLoading,
    selectMultiCreationCampaignsCreating,
    selectMultiCreationVacanciesAuctionInfo,
    setMultiCreationBid,
    setMultiCreationBidFrequencyStatus,
} from 'store/models/multiCreation';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/MultiCreationAuction/BidsAndBudgetsTable/BidsAndBudgetsTableBody/components/BidInput/BidInput.module.less';

const FREQUENCY_ICON = {
    [BidFrequencyStatus.Rare]: ChevronWideOutlinedSize24,
    [BidFrequencyStatus.Average]: ChevronWide2OutlinedSize24,
    [BidFrequencyStatus.Often]: ChevronWide3OutlinedSize24,
};

const TrlKeys = {
    bidPlaceholder: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.placeholder',
    showOftenButton: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.showOftenButton',
    bestBid: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.bestBid',
    lowBid: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.lowBid',
    tooltipTitle: {
        [BidFrequencyStatus.Rare]: 'auction.bidRating.rare.title',
        [BidFrequencyStatus.Average]: 'auction.bidRating.average.title',
        [BidFrequencyStatus.Often]: 'auction.bidRating.often.title',
    },
    error: {
        minValue: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.error.minValue',
        biggerThanBudget: 'MultiCreationAuction.BidsAndBudgetsTable.BidsAndBudgetsTableBody.bid.error.biggerThanBudget',
    },
};

export const BidInput: FC<{ vacancyId: number }> = ({ vacancyId }) => {
    const currentVacanciesAuctionInfo = useSelector(selectMultiCreationVacanciesAuctionInfo)[vacancyId];
    const currentAuctionsToCreate = useSelector(({ multiCreation }) => multiCreation.auctionsToCreate[vacancyId]);
    const biddingStrategy = useSelector(({ multiCreation }) => multiCreation.biddingStrategy);
    const isBidStatusLoading = useSelector(selectMultiCreationBidFrequencyStatusLoading);
    const isCampaignsCreating = useSelector(selectMultiCreationCampaignsCreating);
    const [isVisible, setIsVisibleOn, setIsVisibleOff] = useOnOffState(false);

    const { bid, bidFrequencyStatus, bidValidationError, minBidValue: minBid } = currentAuctionsToCreate;
    const oftenThresholdBid = currentVacanciesAuctionInfo.bidRating.oftenThresholdBid;
    const isBidChangeDisabled = currentVacanciesAuctionInfo.bid.disabled;
    const idBidFrequencyStatusOften = bidFrequencyStatus === BidFrequencyStatus.Often;

    const { isMobile } = useBreakpoint();
    const trls = useTrls();
    const dispatch = useDispatch();
    const activatorRef = useRef(null);
    const { validateBid } = useMultiCreationBidBudgetValidation({ biddingStrategy, vacancyId });

    useEffect(() => {
        validateBid(bid, minBid);
    }, [bid, minBid, validateBid]);

    const handleBidChange = (value: string) => {
        dispatch(setMultiCreationBid({ vacancyId, value }));

        if (Number(value) >= minBid) void dispatch(fetchMultiCreationBidRating({ vacancyId, bid: value }));
    };

    const action = idBidFrequencyStatusOften ? undefined : (
        <Button
            data-qa="apply-often-button"
            stretched
            style={isMobile ? 'accent' : 'constant'}
            mode="primary"
            size="small"
            onClick={() => {
                dispatch([
                    setMultiCreationBid({
                        vacancyId,
                        value: String(oftenThresholdBid),
                    }),
                    setMultiCreationBidFrequencyStatus({
                        vacancyId,
                        bidFrequencyStatus: BidFrequencyStatus.Often,
                    }),
                ]);
            }}
        >
            {trls(TrlKeys.showOftenButton, [currentVacanciesAuctionInfo.bidRating.oftenThresholdBid / 100])}
        </Button>
    );

    const inputProps = {
        'data-qa': 'bid-input',
        buttonRef: activatorRef,
        disabled: isBidChangeDisabled || isBidStatusLoading || isCampaignsCreating,
        elevatePlaceholder: isMobile,
        onChange: handleBidChange,
        placeholder: trls(TrlKeys.bidPlaceholder),
        size: (isMobile ? 'large' : 'medium') as InputSize,
        value: bid,
        invalid: !!bidValidationError,
        errorMessage: bidValidationError || undefined,
    };

    const isBidFrequencyShown = !!bidFrequencyStatus && !bidValidationError && !isBidStatusLoading;

    const iconProps = isBidFrequencyShown
        ? {
              buttonIcon: FREQUENCY_ICON[bidFrequencyStatus],
              onButtonClick: setIsVisibleOn,
          }
        : {};

    return (
        <>
            <div
                className={classNames(
                    styles.wrapper,
                    bidFrequencyStatus && styles[bidFrequencyStatus.toLowerCase() as keyof typeof styles]
                )}
            >
                <MoneyInput {...iconProps} {...inputProps} />
            </div>
            {isBidFrequencyShown && (
                <>
                    <TooltipHover
                        key={bidFrequencyStatus}
                        activatorRef={activatorRef}
                        buttons={action}
                        placement="right-top"
                        title={trls(TrlKeys.tooltipTitle[bidFrequencyStatus])}
                    >
                        {trls(idBidFrequencyStatusOften ? TrlKeys.bestBid : TrlKeys.lowBid)}
                    </TooltipHover>
                    <BottomSheet
                        height="content"
                        visible={isVisible}
                        onClose={setIsVisibleOff}
                        footer={<ActionBar primaryActions={action} />}
                        data-qa="auction-editing-bottom-sheet"
                    >
                        <Text typography="title-5-semibold">{trls(TrlKeys.tooltipTitle[bidFrequencyStatus])}</Text>
                        <VSpacing default={8} />
                        <Text typography="paragraph-2-regular">
                            {trls(idBidFrequencyStatusOften ? TrlKeys.bestBid : TrlKeys.lowBid)}
                        </Text>
                        {idBidFrequencyStatusOften && <VSpacing default={16} />}
                    </BottomSheet>
                </>
            )}
        </>
    );
};
